import React, { useState, useEffect } from 'react';
import { FaLanguage, FaTimes, FaSpinner, FaVolumeUp, FaBook, FaGlobe, FaChevronDown, FaChevronUp, FaCopy, FaRedo } from 'react-icons/fa';
import { translateLyrics } from '../lib/openai';
import { elevenLabsListener } from '../lib/listener';
import toast from 'react-hot-toast';

interface MobileTextAnalysisProps {
  selectedText: string;
  onClose: () => void;
}

type TargetLanguage = 'english' | 'french' | 'haitian creole';

interface Translation {
  language: TargetLanguage;
  text: string;
  explanation?: string;
}

export default function MobileTextAnalysis({ selectedText, onClose }: MobileTextAnalysisProps) {
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<TargetLanguage>('spanish');
  const [showExplanation, setShowExplanation] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const languages: { value: TargetLanguage; label: string; flag: string }[] = [
    { value: 'english', label: 'English', flag: '🇺🇸' },
    { value: 'french', label: 'French', flag: '🇫🇷' },
    { value: 'haitian creole', label: 'Haitian Creole', flag: '🇭🇹' }
  ];

  useEffect(() => {
    // Auto-translate to English when component mounts
    handleTranslate('english');
  }, [selectedText]);

  const handleTranslate = async (targetLanguage: TargetLanguage) => {
    if (!selectedText.trim()) return;

    setLoading(true);
    try {
      // Check if we already have this translation
      const existingTranslation = translations.find(t => t.language === targetLanguage);
      if (existingTranslation) {
        setSelectedLanguage(targetLanguage);
        setLoading(false);
        return;
      }

      // Generate translation with explanation
      const translationPrompt = `Translate this English text to ${targetLanguage} and provide a brief explanation of the meaning:

"${selectedText}"

Please format your response as:
Translation: [translated text]
Explanation: [brief explanation of the meaning and context]`;

      const response = await translateLyrics(translationPrompt, targetLanguage);
      
      // Parse the response to extract translation and explanation
      const lines = response.split('\n');
      let translation = '';
      let explanation = '';
      
      for (const line of lines) {
        if (line.toLowerCase().startsWith('translation:')) {
          translation = line.substring(line.indexOf(':') + 1).trim();
        } else if (line.toLowerCase().startsWith('explanation:')) {
          explanation = line.substring(line.indexOf(':') + 1).trim();
        }
      }

      // If parsing failed, use the whole response as translation
      if (!translation) {
        translation = response;
      }

      const newTranslation: Translation = {
        language: targetLanguage,
        text: translation,
        explanation: explanation || undefined
      };

      setTranslations(prev => [...prev.filter(t => t.language !== targetLanguage), newTranslation]);
      setSelectedLanguage(targetLanguage);
    } catch (error: any) {
      console.error('Translation error:', error);
      toast.error(`Failed to translate to ${targetLanguage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAudio = async () => {
    const currentTranslation = translations.find(t => t.language === selectedLanguage);
    if (!currentTranslation || !elevenLabsListener.isAvailable()) return;

    setGeneratingAudio(true);
    try {
      let audioUrl: string | null = null;

      if (selectedLanguage === 'english') {
        audioUrl = await elevenLabsListener.generateSpanishSpeech(
          currentTranslation.text,
          'maria', // Use Maria voice for Spanish
          0.8,     // Slightly slower for learning
          0.9,     // High stability
          0.9      // High clarity
        );
      } else {
        // For French and Haitian Creole, audio generation is not available
        // For now, show a message that audio is only available for English
        toast.info('Audio generation is currently only available for English translations');
        return;
      }

      if (audioUrl) {
        setAudioUrl(audioUrl);
        // Play the audio
        const audio = new Audio(audioUrl);
        audio.play().catch(error => {
          console.error('Error playing audio:', error);
          toast.error('Failed to play audio');
        });
      }
    } catch (error: any) {
      console.error('Audio generation error:', error);
      toast.error('Failed to generate audio');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const handleCopyTranslation = () => {
    const currentTranslation = translations.find(t => t.language === selectedLanguage);
    if (currentTranslation) {
      navigator.clipboard.writeText(currentTranslation.text);
      toast.success('Translation copied to clipboard!');
    }
  };

  const currentTranslation = translations.find(t => t.language === selectedLanguage);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      
      {/* Mobile Text Analysis Modal */}
      <div className="fixed inset-x-4 bottom-4 top-20 z-50 flex flex-col">
        <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-2xl shadow-2xl border border-gray-600/50 flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-600/50 bg-gradient-to-r from-gray-800/50 to-gray-700/50 flex-shrink-0">
            <div className="flex items-center space-x-2 flex-1">
              <FaBook className="w-5 h-5 text-yellow-400" />
              <h3 className="text-lg font-semibold text-white truncate">Text Analysis</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-2 rounded-full hover:bg-white/10 transition-colors duration-200"
              >
                {isMinimized ? (
                  <FaChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <FaChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 transition-colors duration-200"
              >
                <FaTimes className="w-4 h-4 text-gray-400 hover:text-white" />
              </button>
            </div>
          </div>

          {/* Content - Collapsible */}
          {!isMinimized && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Selected Text */}
              <div className="p-4 border-b border-gray-600/50 flex-shrink-0">
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-600/30">
                  <p className="text-sm text-gray-400 mb-2">Selected Text:</p>
                  <div className="max-h-24 overflow-y-auto">
                    <p className="text-sm text-white font-medium leading-relaxed">{selectedText}</p>
                  </div>
                </div>
              </div>

              {/* Language Selection */}
              <div className="p-4 border-b border-gray-600/50 flex-shrink-0">
                <p className="text-sm text-gray-400 mb-3">Translate to:</p>
                <div className="relative">
                  <button
                    onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                    className="w-full flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-600/30 text-white"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">
                        {languages.find(l => l.value === selectedLanguage)?.flag}
                      </span>
                      <span className="font-medium">
                        {languages.find(l => l.value === selectedLanguage)?.label}
                      </span>
                    </div>
                    <FaChevronDown className={`w-4 h-4 transition-transform ${showLanguageSelector ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showLanguageSelector && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 rounded-lg border border-gray-600/50 shadow-xl z-10">
                      {languages.map((lang) => (
                        <button
                          key={lang.value}
                          onClick={() => {
                            setSelectedLanguage(lang.value);
                            setShowLanguageSelector(false);
                            if (!translations.find(t => t.language === lang.value)) {
                              handleTranslate(lang.value);
                            }
                          }}
                          disabled={loading}
                          className={`w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-700/50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                            selectedLanguage === lang.value ? 'bg-yellow-400/20 text-yellow-400' : 'text-white'
                          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className="text-xl">{lang.flag}</span>
                          <span className="font-medium">{lang.label}</span>
                          {selectedLanguage === lang.value && (
                            <div className="ml-auto w-2 h-2 bg-yellow-400 rounded-full"></div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Translation Content - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <FaSpinner className="w-6 h-6 text-yellow-400 animate-spin mr-3" />
                      <span className="text-gray-300">Translating...</span>
                    </div>
                  ) : currentTranslation ? (
                    <div className="space-y-4">
                      {/* Translation */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-medium text-gray-300 flex items-center">
                            <span className="text-lg mr-2">
                              {languages.find(l => l.value === selectedLanguage)?.flag}
                            </span>
                            Translation:
                          </p>
                          <div className="flex items-center space-x-2">
                            {selectedLanguage === 'spanish' && elevenLabsListener.isAvailable() && (
                              <button
                                onClick={handleGenerateAudio}
                                disabled={generatingAudio}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors duration-200 disabled:opacity-50 border border-green-500/30"
                              >
                                {generatingAudio ? (
                                  <FaSpinner className="w-3 h-3 animate-spin text-green-400" />
                                ) : (
                                  <FaVolumeUp className="w-3 h-3 text-green-400" />
                                )}
                                <span className="text-xs text-green-400">
                                  {generatingAudio ? 'Generating...' : 'Listen'}
                                </span>
                              </button>
                            )}
                            <button
                              onClick={handleCopyTranslation}
                              className="flex items-center space-x-1 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors duration-200 border border-blue-500/30"
                            >
                              <FaCopy className="w-3 h-3 text-blue-400" />
                              <span className="text-xs text-blue-400">Copy</span>
                            </button>
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-yellow-400/20 to-yellow-500/20 rounded-lg p-4 border border-yellow-400/30">
                          <p className="text-white font-medium leading-relaxed">{currentTranslation.text}</p>
                        </div>
                      </div>

                      {/* Explanation */}
                      {currentTranslation.explanation && (
                        <div>
                          <button
                            onClick={() => setShowExplanation(!showExplanation)}
                            className="flex items-center space-x-2 text-sm text-gray-400 hover:text-white mb-3 transition-colors w-full text-left"
                          >
                            <FaGlobe className="w-4 h-4" />
                            <span>{showExplanation ? 'Hide' : 'Show'} Explanation</span>
                            <FaChevronDown className={`w-3 h-3 ml-auto transition-transform ${showExplanation ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {showExplanation && (
                            <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg p-4 border border-blue-500/30">
                              <p className="text-sm text-blue-200 leading-relaxed">{currentTranslation.explanation}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 gap-3 pt-2">
                        <button
                          onClick={() => handleTranslate(selectedLanguage)}
                          disabled={loading}
                          className="flex items-center justify-center px-4 py-3 bg-yellow-500/80 hover:bg-yellow-500 rounded-lg font-medium text-black transition-colors duration-200 disabled:opacity-50"
                        >
                          <FaRedo className="w-4 h-4 mr-2" />
                          Retranslate
                        </button>
                        <button
                          onClick={onClose}
                          className="flex items-center justify-center px-4 py-3 bg-gray-700/50 hover:bg-gray-600/50 rounded-lg font-medium text-white transition-colors duration-200 border border-gray-600/30"
                        >
                          <FaTimes className="w-4 h-4 mr-2" />
                          Close
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FaLanguage className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400">Select a language to translate</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-600/50 flex-shrink-0">
                <div className="bg-gradient-to-r from-gray-800/50 to-gray-700/50 rounded-lg p-3 border border-gray-600/30">
                  <p className="text-xs text-gray-400 text-center leading-relaxed">
                    💡 Tap and hold any text in the chat to analyze and translate
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Minimized State */}
          {isMinimized && (
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FaBook className="w-4 h-4 text-yellow-400" />
                <span className="text-white font-medium">Text Analysis</span>
                {currentTranslation && (
                  <span className="text-xs text-gray-400">
                    ({languages.find(l => l.value === selectedLanguage)?.flag} Ready)
                  </span>
                )}
              </div>
              <button
                onClick={() => setIsMinimized(false)}
                className="p-2 rounded-full hover:bg-white/10 transition-colors duration-200"
              >
                <FaChevronUp className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        /* Custom scrollbar for mobile */
        .overflow-y-auto {
          scrollbar-width: thin;
          scrollbar-color: rgba(75, 85, 99, 0.8) transparent;
        }
        
        .overflow-y-auto::-webkit-scrollbar {
          width: 4px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background-color: rgba(75, 85, 99, 0.8);
          border-radius: 2px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background-color: rgba(107, 114, 128, 1);
        }

        /* Smooth animations */
        .transition-all {
          transition-property: all;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 300ms;
        }

        /* Touch-friendly tap targets */
        button {
          min-height: 44px;
          min-width: 44px;
        }

        /* Prevent text selection during interactions */
        .select-none {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
      `}</style>
    </>
  );
}